import type { TipoAlvoImpacto } from './alvosImpacto'
import { colisao, velocidadeDoEvento, type TipoColisao } from './timelineCinematica'
import type { Vetor3 } from '../geometria/vetor3'

export type RegiaoDanoVeicular = 'frente' | 'traseira' | 'lateralEsquerda' | 'lateralDireita' | 'teto'
export type NivelDanoVeicular = 'leve' | 'moderado' | 'severo'

export interface DanoVeicular {
  regiao: RegiaoDanoVeicular
  nivel: NivelDanoVeicular
  /** Intrusão visual didática, em metros. Não é reconstrução pericial. */
  intrusaoM: number
  /** Largura aproximada da área deformada, em metros. */
  larguraContatoM: number
  alvo: TipoAlvoImpacto
  leitura: string
}

const limitar = (valor: number, minimo: number, maximo: number) =>
  Math.max(minimo, Math.min(maximo, valor))

/**
 * Região atingida a partir da direção do impacto, no referencial do veículo.
 *
 * Nula (sem objeto atingido, como no tombamento/capotamento "puro") vira
 * `teto` — a região que a rolagem de fato deforma. Longitudinal decide entre
 * frente/traseira pelo sinal de Z. Na lateral, a direção é a da FORÇA: ela
 * aponta para longe da face atingida. Força para −X ⇒ contato na face +X, que
 * é o lado esquerdo, do motorista (doutrina adotada: lado do motorista é o
 * lado em que ele dirige).
 */
export function regiaoPeloImpacto(direcao: Vetor3 | null): RegiaoDanoVeicular {
  if (!direcao) return 'teto'
  if (Math.abs(direcao.x) >= Math.abs(direcao.z)) {
    return direcao.x < 0 ? 'lateralEsquerda' : 'lateralDireita'
  }
  return direcao.z < 0 ? 'frente' : 'traseira'
}

/**
 * Traduz os parâmetros declarados do cenário em deformação VISUAL, numa
 * região já escolhida.
 *
 * Não tenta resolver resistência de materiais. O alvo muda a concentração:
 * poste concentra a intrusão; muro distribui; outro veículo fica no meio.
 *
 * A velocidade deixou de ser a constante do tipo e passou a ser a que o aluno
 * escolhe: aumentar a velocidade no painel aumenta a intrusão desenhada, que é
 * metade da lição — a outra metade é a energia do corpo, que cresce ao quadrado.
 */
export interface OpcoesDanoNaRegiao {
  /**
   * Resposta da região atingida: a lateral tem menos espaço de absorção que a
   * frente, então a mesma energia visual vira mais intrusão. Fator didático.
   */
  fatorEstrutura?: number
  /**
   * Piso da intrusão. O padrão (12 cm) garante que um impacto contra objeto
   * fixo sempre apareça; entre dois veículos a velocidade RELATIVA pode ser
   * baixa (engavetamento lento), e um piso alto apagaria essa diferença.
   */
  intrusaoMinimaM?: number
  /** largura de contato já conhecida (sobreposição entre dois veículos) */
  larguraContatoM?: number
}

export function danoNaRegiao(
  regiao: RegiaoDanoVeicular,
  alvo: TipoAlvoImpacto,
  velocidadeKmh: number,
  opcoes: OpcoesDanoNaRegiao = {},
): DanoVeicular {
  const fatorAlvo = alvo === 'poste' ? 1.25 : alvo === 'muro' ? 0.8 : 1
  const fatorEstrutura = opcoes.fatorEstrutura ?? 1
  // Escala visual acentuada: com 0,24 m a 50 km/h o amassado mal aparecia no
  // celular. Continua monotônica na velocidade e no alvo, que é a lição.
  const intrusaoM = limitar(
    (velocidadeKmh / 50) * 0.4 * fatorAlvo * fatorEstrutura,
    opcoes.intrusaoMinimaM ?? 0.12,
    0.8,
  )
  const nivel: NivelDanoVeicular =
    intrusaoM >= 0.5 ? 'severo' : intrusaoM >= 0.28 ? 'moderado' : 'leve'
  const larguraContatoM =
    opcoes.larguraContatoM ?? (alvo === 'poste' ? 0.45 : alvo === 'muro' ? 1.7 : 1.35)
  const nomeRegiao =
    regiao === 'lateralEsquerda'
      ? 'lateral esquerda'
      : regiao === 'lateralDireita'
        ? 'lateral direita'
        : regiao

  return {
    regiao,
    nivel,
    intrusaoM,
    larguraContatoM,
    alvo,
    leitura:
      `Deformação ${nivel} na ${nomeRegiao}, ` +
      `com intrusão visual aproximada de ${Math.round(intrusaoM * 100)} cm. ` +
      'Representação didática, não reconstrução pericial.',
  }
}

export function calcularDanoVeicular(
  tipo: TipoColisao,
  alvo: TipoAlvoImpacto,
  velocidadeKmh?: number,
): DanoVeicular {
  const velocidade = velocidadeDoEvento(tipo, velocidadeKmh)
  const regiao = regiaoPeloImpacto(colisao(tipo).direcaoImpacto)
  return danoNaRegiao(regiao, alvo, velocidade)
}

/**
 * Dano do SEGUNDO contato, quando o tipo declara `impactoSecundario` — o
 * engavetamento tem duas zonas de deformação, não uma. `null` para todo
 * tipo que não declara um segundo impacto.
 */
export function calcularDanoSecundario(
  tipo: TipoColisao,
  velocidadeKmh?: number,
): DanoVeicular | null {
  const secundario = colisao(tipo).impactoSecundario
  if (!secundario) return null
  const velocidade = velocidadeDoEvento(tipo, velocidadeKmh)
  const regiao = regiaoPeloImpacto(secundario.direcaoImpacto)
  return danoNaRegiao(regiao, secundario.alvo, velocidade)
}
